import IconClipboard from '@apollo/icons/default/IconClipboard.svg';
import {
  Button,
  ButtonGroup,
  Code,
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
} from '@apollo/orbit';
import classnames from 'classnames';
import copy from 'copy-to-clipboard';
import React, { useEffect, useMemo, useState } from 'react';

import { ClickableText } from 'src/components/common/clickableText/ClickableText';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { TooltipWithMutableContent } from 'src/components/TooltipWithMutableContent';
import { useLocalStorage } from 'src/hooks/useLocalStorage';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { assertUnreachable } from 'src/lib/assertUnreachable';
import { DocsRouter } from 'src/lib/routers';

import { useInternalGraphLinking } from '../../hooks/useInternalGraphLinking';

import { CDNSnippet } from './CDNSnippet';
import { ReactNpmSnippet } from './ReactNpmSnippet';
import { VanillaNpmSnippet } from './VanillaNpmSnippet';

const tabs: Record<'react' | 'vanilla' | 'cdn', string> = {
  react: 'React',
  vanilla: 'Javascript',
  cdn: 'Built file on CDN',
};

export const EmbedSandboxModal = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const [sandboxUrl] = useLocalStorage('sandboxUrl');
  const [tab, setTab] = useState<keyof typeof tabs>('react');

  const { currentExplorerRouteConfig } = useInternalGraphLinking();
  const { embedModalTabState } = useRouteParams(currentExplorerRouteConfig);
  const {
    document: embedDocument,
    variables,
    headers,
    includeCookies,
  } = embedModalTabState || {};

  const shouldPrefillEmbedConfigWithTabState = !!(
    embedDocument ||
    headers ||
    variables ||
    includeCookies
  );

  const [
    showCodeSnippetCopiedConfirmation,
    setShowCodeSnippetCopiedConfirmation,
  ] = useState(false);

  useEffect(() => {
    if (showCodeSnippetCopiedConfirmation) {
      const timeout = setTimeout(() => {
        setShowCodeSnippetCopiedConfirmation(false);
      }, 1000);
      return () => clearTimeout(timeout);
    }
  }, [showCodeSnippetCopiedConfirmation]);

  const snippetContent = useMemo(() => {
    const embedCodeSnippetProps = {
      initialEndpoint: sandboxUrl,
      ...(shouldPrefillEmbedConfigWithTabState
        ? {
            initialState: {
              document: embedDocument,
              headers,
              variables,
              includeCookies,
            },
          }
        : {}),
    };

    switch (tab) {
      case 'react':
        return <ReactNpmSnippet {...embedCodeSnippetProps} />;
      case 'vanilla':
        return <VanillaNpmSnippet {...embedCodeSnippetProps} />;
      case 'cdn':
        return <CDNSnippet {...embedCodeSnippetProps} />;
      default:
        assertUnreachable(tab);
    }
  }, [
    embedDocument,
    headers,
    includeCookies,
    sandboxUrl,
    shouldPrefillEmbedConfigWithTabState,
    tab,
    variables,
  ]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="3xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <ModalTitle>Embed Sandbox</ModalTitle>
          <ModalDescription>
            {tab === 'react' || tab === 'vanilla' ? (
              <>
                <TooltipWithMutableContent initialContent="Copy">
                  {({ setContent, resetContent }) => (
                    <Button
                      variant="hidden"
                      size="sm"
                      className="group h-auto p-0"
                      onClick={() => {
                        copy('npm i @apollo/sandbox');
                        setContent('Copied');
                      }}
                      onMouseEnter={() => resetContent()}
                    >
                      <Code className="group-hover:bg-btn-secondary-hover group-active:bg-btn-secondary-selected">
                        npm i @apollo/sandbox
                      </Code>
                    </Button>
                  )}
                </TooltipWithMutableContent>
                , then paste this code snippet to embed.{' '}
              </>
            ) : (
              <>
                Copy and paste this code snippet to embed this Sandbox on a
                webpage.{' '}
              </>
            )}
            <ClickableText
              className="text-link"
              target="_blank"
              href={DocsRouter.path('EmbeddedSandbox')}
              as="a"
            >
              Learn more.
            </ClickableText>
          </ModalDescription>
        </ModalHeader>
        <ModalBody>
          <div className="flex min-h-0 flex-col">
            <div role="tablist" className="relative z-10 flex gap-2">
              {Object.entries(tabs).map(([key, value]) => (
                <Button
                  key={key}
                  size="sm"
                  variant="hidden"
                  role="tab"
                  onClick={() => setTab(key as keyof typeof tabs)}
                  className={classnames(
                    tab === key
                      ? 'border-b-transparent bg-primary text-primary'
                      : 'border-b-primary bg-disabled text-secondary hover:text-black',
                    'h-8 rounded-b-none rounded-t border border-primary py-1 text-base font-normal',
                  )}
                >
                  {value}
                </Button>
              ))}
            </div>
            <div className="relative -mt-px overflow-auto rounded-b border border-primary bg-primary py-2">
              <TooltipWithMutableContent initialContent="Copy code snippet">
                {({ setContent, resetContent }) => (
                  <div className="sticky top-0 -mt-7 ml-auto mr-2 w-7">
                    <IconButton
                      aria-label="Copy cdn codeblock"
                      size="sm"
                      variant="hidden"
                      icon={<IconClipboard />}
                      onClick={() => {
                        copy(
                          window.document?.getElementById?.(
                            'embed-code-snippet-content',
                          )?.innerText || '',
                        );
                        setContent('Code snippet copied ✨');
                      }}
                      onMouseEnter={() => resetContent()}
                      data-analytics-label="Embeddable Sandbox: Copy cdn codeblock"
                      data-analytics-category="Embeddable Sandbox"
                    />
                  </div>
                )}
              </TooltipWithMutableContent>
              <pre
                id="embed-code-snippet-content"
                className="max-h-80 whitespace-pre-wrap pl-4 text-sm text-neutral"
              >
                {snippetContent}
              </pre>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <ButtonGroup>
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
            <Tooltip
              disabled={!showCodeSnippetCopiedConfirmation}
              label="Code snippet copied ✨"
            >
              <Button
                variant="primary"
                type="button"
                data-analytics-label="Embed Sandbox Modal: Copied code snippet"
                data-analytics-category="Embeddable Sandbox"
                onClick={() => {
                  copy(
                    window.document?.getElementById?.(
                      'embed-code-snippet-content',
                    )?.innerText || '',
                  );
                  setShowCodeSnippetCopiedConfirmation(true);
                }}
                isDisabled={showCodeSnippetCopiedConfirmation}
              >
                {showCodeSnippetCopiedConfirmation ? 'Copied' : 'Copy'}
              </Button>
            </Tooltip>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
